import { useMemo, useState } from 'react';
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { cn } from '@/lib/utils';
import { useMembers } from '@/features/members';
import { AllocationFormDialog } from '../components/AllocationFormDialog';
import {
  useAllocations,
  useAllocationUtilization,
  useCreateAllocation,
  useDeleteAllocation,
  useProjects,
  useUpdateAllocation,
} from '../queries';
import type { PersonUtilization } from '../api';
import type { Allocation, AllocationInput } from '../types';

export function AllocationsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Allocation | undefined>(undefined);
  const [toDelete, setToDelete] = useState<Allocation | null>(null);

  const [asOf, setAsOf] = useState('');

  const { data, isPending, isError, error, isFetching } = useAllocations();
  const { data: projectsData } = useProjects({});
  const { data: membersData } = useMembers();
  const { data: utilization } = useAllocationUtilization(asOf || undefined);

  const overUserIds = useMemo(
    () => new Set((utilization?.people ?? []).filter((p) => p.over_allocated).map((p) => p.user)),
    [utilization],
  );

  const projectNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of projectsData?.results ?? []) map.set(p.id, p.name);
    return map;
  }, [projectsData]);
  const userNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of membersData?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [membersData]);

  const create = useCreateAllocation();
  const update = useUpdateAllocation();
  const remove = useDeleteAllocation();

  const allocations = data?.results ?? [];

  async function handleSubmit(input: AllocationInput) {
    if (editing) await update.mutateAsync({ id: editing.id, input });
    else await create.mutateAsync(input);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Resource Allocation</h1>
        <Button
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-4" /> New allocation
        </Button>
      </div>
      <p className="text-muted-foreground text-sm">
        Who&rsquo;s working on what, and at what weekly capacity.
      </p>

      <CapacityPanel
        people={utilization?.people ?? []}
        asOf={asOf}
        onAsOfChange={setAsOf}
        defaultAsOf={utilization?.as_of}
      />

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load allocations.'}
        </p>
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Person</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Hours / week</TableHead>
                <TableHead>Window</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : allocations.length ? (
                allocations.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">
                      {projectNames.get(a.project) ?? `#${a.project}`}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5">
                        {userNames.get(a.user) ?? '…'}
                        {overUserIds.has(a.user) ? (
                          <AlertTriangle
                            className="size-3.5 text-red-500"
                            aria-label="Over-allocated"
                          />
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{a.role || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.hours_per_week}</TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {a.start_date ? new Date(a.start_date).toLocaleDateString() : '—'}
                      {' → '}
                      {a.end_date ? new Date(a.end_date).toLocaleDateString() : 'open'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Edit allocation"
                        onClick={() => {
                          setEditing(a);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Delete allocation"
                        onClick={() => setToDelete(a)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground text-center">
                    No allocations yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <AllocationFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        allocation={editing}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove allocation?</AlertDialogTitle>
            <AlertDialogDescription>
              This won&rsquo;t delete any logged hours.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capacity analytics (ADR-072)
// ---------------------------------------------------------------------------
function CapacityPanel({
  people,
  asOf,
  onAsOfChange,
  defaultAsOf,
}: {
  people: PersonUtilization[];
  asOf: string;
  onAsOfChange: (v: string) => void;
  defaultAsOf?: string;
}) {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <section className="rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold">Capacity</h2>
          <p className="text-muted-foreground text-sm">
            Weekly utilization per person, as of the selected date.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">As of</Label>
            <Input
              type="date"
              value={asOf || defaultAsOf || ''}
              onChange={(e) => onAsOfChange(e.target.value)}
              className="h-8 w-40"
            />
          </div>
        </div>
      </div>

      {people.length === 0 ? (
        <p className="text-muted-foreground p-4 text-sm">
          No one is allocated on this date — add allocations to see utilization.
        </p>
      ) : (
        <ul className="divide-y">
          {people.map((p) => (
            <li key={p.user}>
              <button
                type="button"
                onClick={() => setExpanded(expanded === p.user ? null : p.user)}
                className="hover:bg-muted/40 flex w-full items-center gap-3 px-4 py-2.5 text-left"
              >
                <span className="w-40 shrink-0 truncate text-sm font-medium">{p.name}</span>
                <CapacityBar pct={p.utilization_pct} over={p.over_allocated} />
                <span className="w-44 shrink-0 text-right text-xs tabular-nums">
                  <span className={cn(p.over_allocated && 'text-red-600')}>
                    {fmt(p.allocated)} / {fmt(p.capacity)}h · {p.utilization_pct}%
                  </span>
                  {p.over_allocated ? (
                    <span className="text-red-600"> · over by {fmt(p.over_by)}h</span>
                  ) : null}
                </span>
              </button>
              {expanded === p.user ? (
                <ul className="bg-muted/20 space-y-1 px-4 pb-2.5 pl-44 text-xs">
                  {p.by_project.map((b, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-muted-foreground flex-1 truncate">
                        {b.project_name}
                        {b.role ? ` · ${b.role}` : ''}
                      </span>
                      <span className="tabular-nums">{fmt(b.hours_per_week)}h/wk</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CapacityBar({ pct, over }: { pct: number; over: boolean }) {
  const width = Math.min(100, pct);
  const color = over ? 'bg-red-500' : pct >= 85 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="bg-muted relative h-2.5 flex-1 overflow-hidden rounded-full">
      <div className={cn('h-full rounded-full', color)} style={{ width: `${width}%` }} />
    </div>
  );
}

/** Trim trailing `.00` from a decimal string for compact display. */
function fmt(value: string): string {
  return value.replace(/\.00$/, '');
}
